import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface PackageFeatures {
  title: string;
  price: string;
  badge: string;
  features: string[];
  whoFor: string;
  isHighlighted: boolean;
}

export default function OfferingSection() {
  const { t } = useTranslation();

  const packages: PackageFeatures[] = [
    {
      title: t('servicesPage.packages.1.title'),
      price: t('servicesPage.packages.1.price'),
      badge: t('servicesPage.packages.1.badge'),
      features: [
        t('servicesPage.packages.1.feature1'),
        t('servicesPage.packages.1.feature2'),
        t('servicesPage.packages.1.feature3'),
      ],
      whoFor: t('servicesPage.packages.1.whoFor'),
      isHighlighted: false,
    },
    {
      title: t('servicesPage.packages.2.title'),
      price: t('servicesPage.packages.2.price'),
      badge: t('servicesPage.packages.2.badge'),
      features: [
        t('servicesPage.packages.2.feature1'),
        t('servicesPage.packages.2.feature2'),
        t('servicesPage.packages.2.feature3'),
        t('servicesPage.packages.2.feature4'),
        t('servicesPage.packages.2.feature5'),
        t('servicesPage.packages.2.feature6'),
      ],
      whoFor: t('servicesPage.packages.2.whoFor'),
      isHighlighted: true,
    },
    {
      title: t('servicesPage.packages.3.title'),
      price: t('servicesPage.packages.3.price'),
      badge: t('servicesPage.packages.3.badge'),
      features: [
        t('servicesPage.packages.3.feature1'),
        t('servicesPage.packages.3.feature2'),
        t('servicesPage.packages.3.feature3'),
        t('servicesPage.packages.3.feature4'),
        t('servicesPage.packages.3.feature5'),
        t('servicesPage.packages.3.feature6'),
        t('servicesPage.packages.3.feature7'),
      ],
      whoFor: t('servicesPage.packages.3.whoFor'),
      isHighlighted: false,
    },
  ];

  return (
    <section className="py-20 md:py-28 px-6 md:px-10 lg:px-16 bg-background-50">
      <div className="max-w-[1280px] mx-auto">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4">
            {t('servicesPage.packagesTitle')}
          </h2>
          <p className="text-sm text-foreground-500 max-w-[620px] mx-auto leading-relaxed">
            {t('servicesPage.packagesSubtitle')}
          </p>
        </div>

        <div className="relative max-w-[960px] mx-auto">
          <div className="absolute left-[31px] top-8 bottom-8 w-[2px] bg-background-200/70 hidden md:block" />

          <div className="flex flex-col gap-12">
            {packages.map((pkg, idx) => (
              <div key={idx} className="relative flex flex-col md:flex-row gap-5 md:gap-0">
                <div className="flex md:flex-col items-center md:items-start gap-0 shrink-0 md:w-[68px] z-10">
                  <div
                    className={`w-[64px] h-[64px] rounded-2xl flex items-center justify-center text-xl font-bold shrink-0 transition-all ${
                      pkg.isHighlighted
                        ? 'bg-accent-500 text-white shadow-[0_0_0_6px_var(--accent-100)]'
                        : 'bg-background-100 text-foreground-700 border-2 border-background-200/70'
                    }`}
                  >
                    {idx + 1}
                  </div>
                </div>

                <div
                  className={`flex-1 rounded-2xl border bg-white overflow-hidden transition-all duration-300 ${
                    pkg.isHighlighted
                      ? 'border-accent-300 shadow-[0_2px_24px_rgba(0,0,0,0.06)]'
                      : 'border-background-200/70'
                  }`}
                >
                  <div className="flex flex-col lg:flex-row">
                    <div className="flex-1 p-7 md:p-8">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                          {pkg.title}
                        </h3>
                        <span
                          className={`inline-block px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                            pkg.isHighlighted
                              ? 'bg-accent-500 text-white'
                              : 'bg-accent-100 text-primary-700'
                          }`}
                        >
                          {pkg.badge}
                        </span>
                      </div>

                      <p className="text-2xl md:text-3xl font-bold text-primary-500 mb-5">
                        {pkg.price}
                      </p>

                      <ul className="space-y-3">
                        {pkg.features.map((feature, fIdx) => (
                          <li key={fIdx} className="flex items-start gap-3">
                            <div className="w-5 h-5 rounded-full bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
                              <i className="ri-check-line text-accent-600 text-xs" />
                            </div>
                            <span className="text-sm text-foreground-600 leading-relaxed">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="hidden lg:block w-[1px] bg-background-200/70 self-stretch" />

                    <div className="lg:w-[340px] shrink-0 bg-background-50/70 p-7 md:p-8 border-t lg:border-t-0 border-background-200/70">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                          <i className="ri-user-heart-line text-accent-600 text-sm" />
                        </div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-foreground-400">
                          Who It's For
                        </span>
                      </div>
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        {pkg.whoFor}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-[960px] mx-auto mt-16 text-center">
          <p className="text-sm text-foreground-500 mb-6 leading-relaxed">
            {t('servicesPage.packages.cta')}
          </p>
          <Link
            to="/booking"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-white font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {t('servicesPage.packages.ctaButton')}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}